import { formatRankMetric } from '@/lib/ranks/formatRankMetric';
import { joinRankMetricUnit } from '@/lib/ranks/rankMetricUnit';

export type RankTierRequirement =
  | { kind: 'views'; metric: string }
  | { kind: 'money'; metric: string }
  | { kind: 'campaigns'; metric: string }
  | { kind: 'clips'; metric: string }
  | { kind: 'affiliate_sale' }
  | { kind: 'season_exact'; placement: number }
  | { kind: 'season_range'; from: number; to: number };

const TIER_REQUIREMENTS: Record<string, RankTierRequirement> = {
  'views.bronze': { kind: 'views', metric: '300000' },
  'views.silver': { kind: 'views', metric: '500000' },
  'views.gold': { kind: 'views', metric: '900000' },
  'views.platinum': { kind: 'views', metric: '1500000' },
  'views.master': { kind: 'views', metric: '5000000' },
  'views.legend': { kind: 'views', metric: '10000000' },
  'campaign_money.bronze': { kind: 'money', metric: '50000' },
  'campaign_money.silver': { kind: 'money', metric: '100000' },
  'campaign_money.gold': { kind: 'money', metric: '200000' },
  'campaign_money.black': { kind: 'money', metric: '450000' },
  'affiliate.bronze': { kind: 'affiliate_sale' },
  'affiliate.silver': { kind: 'money', metric: '40000' },
  'affiliate.gold': { kind: 'money', metric: '60000' },
  'affiliate.six_digits': { kind: 'money', metric: '100000' },
  'campaigns.bronze': { kind: 'campaigns', metric: '5' },
  'campaigns.silver': { kind: 'campaigns', metric: '20' },
  'campaigns.gold': { kind: 'campaigns', metric: '50' },
  'renders.bronze': { kind: 'clips', metric: '50' },
  'renders.silver': { kind: 'clips', metric: '100' },
  'renders.gold': { kind: 'clips', metric: '500' },
  'renders.diamond': { kind: 'clips', metric: '1000' },
  'season.emperor': { kind: 'season_exact', placement: 1 },
  'season.elite': { kind: 'season_range', from: 2, to: 5 },
  'season.master': { kind: 'season_range', from: 6, to: 20 },
  'season.veteran': { kind: 'season_range', from: 21, to: 50 },
};

export function rankTierRequirement(tierId: string): RankTierRequirement | null {
  return TIER_REQUIREMENTS[tierId] ?? null;
}

function formatCountableRequirement(
  trackId: 'views' | 'campaign_money' | 'affiliate' | 'campaigns' | 'renders',
  metric: string,
  language: string,
  unit: string | null,
): string {
  return joinRankMetricUnit(
    formatRankMetric({ trackId, metric, language }),
    unit,
  );
}

export function formatRankTierRequirement({
  tierId,
  language,
  t,
  seasonName,
}: {
  tierId: string;
  language: string;
  t: (key: string, values?: Record<string, string | number>) => string;
  seasonName?: string | null;
}): string | null {
  const requirement = rankTierRequirement(tierId);
  if (!requirement) return null;

  if (requirement.kind === 'views') {
    return t('ranks.requirements.views', {
      metric: formatCountableRequirement('views', requirement.metric, language, t('ranks.units.views')),
    });
  }
  if (requirement.kind === 'money') {
    const trackId = tierId.startsWith('affiliate.') ? 'affiliate' : 'campaign_money';
    return t('ranks.requirements.money', {
      metric: formatCountableRequirement(trackId, requirement.metric, language, null),
    });
  }
  if (requirement.kind === 'campaigns') {
    return t('ranks.requirements.campaigns', {
      metric: formatCountableRequirement(
        'campaigns',
        requirement.metric,
        language,
        t('ranks.units.campaigns'),
      ),
    });
  }
  if (requirement.kind === 'clips') {
    return t('ranks.requirements.clips', {
      metric: formatCountableRequirement(
        'renders',
        requirement.metric,
        language,
        t('ranks.units.clips'),
      ),
    });
  }
  if (requirement.kind === 'affiliate_sale') {
    return t('ranks.requirements.affiliateBronze');
  }
  if (requirement.kind === 'season_exact') {
    if (seasonName) {
      return t('ranks.requirements.seasonExactNamed', {
        placement: requirement.placement,
        season: seasonName,
      });
    }
    return t('ranks.requirements.seasonExact', { placement: requirement.placement });
  }
  if (seasonName) {
    return t('ranks.requirements.seasonRangeNamed', {
      from: requirement.from,
      to: requirement.to,
      season: seasonName,
    });
  }
  return t('ranks.requirements.seasonRange', {
    from: requirement.from,
    to: requirement.to,
  });
}
